import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, AlertTriangle, CreditCard, Calendar, Info, ExternalLink, MessageCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { db } from '../../config/firebase';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';

interface Notification {
  id: string;
  userId: string;
  businessId: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'membership' | 'pass' | 'waitlist_promotion' | 'chat_message';
  isRead: boolean;
  createdAt: Date;
  metadata?: {
    type?: string;
    requestingUserId?: string;
    requestingUserEmail?: string;
    requestingUserName?: string;
    link?: string;
    chatUserId?: string;
  };
}

interface NotificationBellProps {
  className?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ className }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { businessId } = useBusiness();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [chatNotifications, setChatNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  // Manejar clic en notificación - navegar al lugar correspondiente
  const handleNotificationClick = (notification: Notification) => {
    // Determinar la ruta según el tipo de notificación
    let targetRoute = '';

    // Primero revisar metadata
    if (notification.metadata?.link) {
      targetRoute = notification.metadata.link;
    } else if (notification.type === 'chat_message' && notification.metadata?.chatUserId) {
      // Redirigir al chat del usuario
      targetRoute = `/admin/users?openChat=${notification.metadata.chatUserId}`;
    } else if (notification.metadata?.type === 'pending_user_approval') {
      // Ir a usuarios con filtro de pendientes
      targetRoute = '/admin/users?status=pending_approval';
    } else {
      // Fallback por tipo de notificación
      switch (notification.type) {
        case 'membership':
        case 'pass':
          targetRoute = '/memberships';
          break;
        case 'waitlist_promotion':
          targetRoute = '/calendar';
          break;
        case 'warning':
          // Si es un warning sin metadata específico, probablemente sea de usuarios
          targetRoute = '/admin/users';
          break;
        default:
          targetRoute = '/dashboard';
      }
    }

    // Cerrar el panel y navegar
    setIsOpen(false);
    if (targetRoute) {
      navigate(targetRoute);
    }

    // Marcar como leída (eliminar)
    handleDismiss(notification.id);
  };

  // Escuchar notificaciones no leídas en tiempo real
  useEffect(() => {
    if (!currentUser?.id || !businessId) {
      console.log('🔔 NotificationBell: No user or business', { userId: currentUser?.id, businessId });
      return;
    }

    console.log('🔔 NotificationBell: Escuchando notificaciones para', { userId: currentUser.id, businessId });

    // Consulta simplificada para evitar necesidad de índice compuesto
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', currentUser.id),
      where('isRead', '==', false)
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      console.log('🔔 NotificationBell: Recibidas', snapshot.docs.length, 'notificaciones');

      // Filtrar por businessId en el cliente y ordenar
      const notifs = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }))
        .filter((n: any) => n.businessId === businessId)
        .sort((a: any, b: any) => b.createdAt.getTime() - a.createdAt.getTime()) as Notification[];

      console.log('🔔 NotificationBell: Filtradas para este negocio:', notifs.length);
      setNotifications(notifs);
    }, (error) => {
      console.error('🔔 Error escuchando notificaciones:', error);
    });

    // Listener de chats (Solo admins)
    let unsubscribeChats = () => { };

    if (currentUser.role === 'admin' || currentUser.role === 'super_admin') {
      const chatsQuery = query(
        collection(db, 'chats'),
        where('businessId', '==', businessId),
        where('senderRole', '==', 'user'),
        where('isRead', '==', false)
      );

      unsubscribeChats = onSnapshot(chatsQuery, (snapshot) => {
        const chatNotifs = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: `chat-${doc.id}`, // Prefijo para evitar colisiones
            userId: currentUser.id, // Dummy, no importa para display
            businessId: businessId,
            title: `Mensaje de ${data.senderName}`,
            message: data.text,
            type: 'chat_message',
            isRead: false,
            createdAt: data.createdAt?.toDate() || new Date(),
            metadata: {
              chatUserId: data.senderId
            }
          } as Notification;
        });

        // Ordenar chats
        chatNotifs.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setChatNotifications(chatNotifs);
      }, (error) => {
        console.error('🔔 Error escuchando chats:', error);
      });
    }

    return () => {
      unsubscribe();
      unsubscribeChats();
    };
  }, [currentUser?.id, businessId, currentUser?.role]);

  // Marcar como leída y eliminar
  const handleDismiss = async (notificationId: string) => {
    // Los mensajes de chat se marcan como leídos al abrir el chat, no aquí
    if (notificationId.startsWith('chat-')) return;

    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
    } catch (error) {
      console.error('Error eliminando notificación:', error);
    }
  };

  // Marcar todas como leídas
  const handleDismissAll = async () => {
    try {
      await Promise.all(
        notifications.map(n => deleteDoc(doc(db, 'notifications', n.id)))
      );
      setIsOpen(false);
    } catch (error) {
      console.error('Error eliminando notificaciones:', error);
    }
  };

  // Obtener icono según el tipo
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-amber-400" />;
      case 'membership':
      case 'pass':
        return <CreditCard className="w-5 h-5 text-primary-400" />;
      case 'success':
      case 'waitlist_promotion':
        return <Calendar className="w-5 h-5 text-green-400" />;
      case 'chat_message':
        return <MessageCircle className="w-5 h-5 text-indigo-400" />;
      default:
        return <Info className="w-5 h-5 text-blue-400" />;
    }
  };

  // Obtener color de fondo según el tipo
  const getNotificationBg = (type: string) => {
    switch (type) {
      case 'warning':
        return 'bg-amber-500/10 border-amber-500/30';
      case 'membership':
      case 'pass':
        return 'bg-primary-500/10 border-primary-500/30';
      case 'success':
      case 'waitlist_promotion':
        return 'bg-green-500/10 border-green-500/30';
      case 'chat_message':
        return 'bg-indigo-500/10 border-indigo-500/30';
      default:
        return 'bg-blue-500/10 border-blue-500/30';
    }
  };

  const allNotifications = [...chatNotifications, ...notifications].sort(
    (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
  );

  const unreadCount = allNotifications.length;

  if (!currentUser) return null;

  return (
    <div className={`relative ${className}`}>
      {/* Botón de notificaciones */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-400 hover:text-white transition-colors rounded-xl hover:bg-gray-800"
      >
        <Bell className={`w-6 h-6 ${unreadCount > 0 ? 'text-amber-400' : ''}`} />

        {/* Badge con animación de pulso */}
        {unreadCount > 0 && (
          <>
            {/* Círculo animado de fondo */}
            <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full animate-ping opacity-75" />
            {/* Badge principal */}
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold shadow-lg">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          </>
        )}
      </button>

      {/* Panel de notificaciones */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-gray-900 rounded-2xl shadow-2xl border border-gray-800 z-50 overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-400" />
                <h3 className="text-lg font-semibold text-white">
                  Notificaciones
                </h3>
                {unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Lista de notificaciones */}
            <div className="max-h-80 overflow-y-auto">
              {allNotifications.length > 0 ? (
                <div className="p-2 space-y-2">
                  {allNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`p-4 rounded-xl border ${getNotificationBg(notification.type)} transition-all hover:scale-[1.02] cursor-pointer group`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-white group-hover:text-amber-300 transition-colors">
                            {notification.title}
                          </h4>
                          <p className="text-sm text-gray-400 mt-1">
                            {notification.message}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-xs text-gray-500">
                              {notification.createdAt.toLocaleDateString('es-ES', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                            <span className="text-xs text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                              Ver <ExternalLink className="w-3 h-3" />
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDismiss(notification.id); }}
                          className="flex-shrink-0 p-1 text-gray-500 hover:text-white transition-colors rounded-lg hover:bg-gray-700"
                          title="Descartar"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Bell className="w-8 h-8 text-gray-600" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">
                    Todo al día
                  </h3>
                  <p className="text-gray-500 text-sm">
                    No tienes notificaciones pendientes
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            {allNotifications.length > 0 && (
              <div className="p-3 border-t border-gray-800">
                <button
                  onClick={handleDismissAll}
                  className="w-full py-2 px-4 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-xl transition-colors"
                >
                  Descartar todas
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
